import React, {FormEvent, useEffect, useMemo, useRef, useState} from 'react';

import {addYears, isAfter, subDays} from 'date-fns';
import {saveAs} from 'file-saver';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as ExportIcon} from 'assets/svg/download.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {DateRange} from 'components/ui-kit/DateRange';
import {Icon} from 'components/ui-kit/Icon';
import {Popover} from 'components/ui-kit/Popover';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {
    ButtonsContainer,
    CloseButton,
    HeaderWrapper,
} from 'components/ui-kit/Table2.0/HeaderWrapper/Filtration/styled';
import {RestError, showRestError} from 'core/errors';
import {AuditExportFormat as RecallAuditExportFormat} from 'types/audit-log';

import {ExportFields, ExportFilterContent, ExportPopoverAnchor} from './styled';

type ExportPeriod = [Date | null, Date | null];

interface ExportControlProps {
    initialPeriod?: ExportPeriod;
    onExport: (
        format: RecallAuditExportFormat,
        period: ExportPeriod,
    ) => Promise<{data: Blob; truncated?: boolean; limit?: number}>;
    fileName: string;
}

interface ExportFormProps {
    initialPeriod: ExportPeriod;
    pending: boolean;
    onCancel: () => void;
    onExport: (format: RecallAuditExportFormat, period: ExportPeriod) => void;
}

const ExportForm = ({initialPeriod, pending, onCancel, onExport}: ExportFormProps) => {
    const {t} = useTranslation();
    const [format, setFormat] = useState<RecallAuditExportFormat | null>(null);
    const [period, setPeriod] = useState<ExportPeriod>(initialPeriod);
    const [periodRevision, setPeriodRevision] = useState(0);
    const formatOptions = useMemo(
        () =>
            Object.values(RecallAuditExportFormat).map((value) => ({
                value,
                label: value,
            })),
        [],
    );

    const submit = (event: FormEvent) => {
        event.preventDefault();
        if (format && period) onExport(format, period);
    };
    const changePeriod = (value: ExportPeriod | undefined) => {
        setPeriod(value ?? [null, null]);
        setPeriodRevision((revision) => revision + 1);
    };
    return (
        <form onSubmit={submit}>
            <ExportFields>
                <Select
                    label={t('pages.recallAudit.export.format')}
                    options={formatOptions}
                    value={formatOptions.find((option) => option.value === format)}
                    onChange={(option: SelectOption<RecallAuditExportFormat>) =>
                        setFormat(option?.value ?? null)
                    }
                />
                <DateRange
                    key={periodRevision}
                    label={t('pages.recallAudit.export.period')}
                    value={period}
                    onChange={changePeriod}
                    keepHeaderSelectOpenOnBlur
                />
            </ExportFields>
            <ButtonsContainer>
                <Button
                    size="sm"
                    variant={ButtonVariant.Outline}
                    disabled={pending}
                    type="button"
                    onClick={onCancel}
                >
                    {t('components.form.Cancel')}
                </Button>
                <Button
                    size="sm"
                    variant={ButtonVariant.Normal}
                    isLoading={pending}
                    disabled={!format || !period?.[0] || !period?.[1]}
                    type="submit"
                >
                    {t('pages.recallAudit.export.action')}
                </Button>
            </ButtonsContainer>
        </form>
    );
};

export const AuditExportControl = React.memo(
    ({initialPeriod: selectedPeriod, onExport, fileName}: ExportControlProps) => {
        const {t} = useTranslation();
        const anchorRef = useRef<HTMLDivElement>(null);
        const [, setAnchorReady] = useState(false);
        const [opened, setOpened] = useState(false);
        const [pending, setPending] = useState(false);
        const initialPeriod = useMemo<ExportPeriod>(() => {
            const filterPeriod = selectedPeriod;

            if (filterPeriod?.[0] && filterPeriod?.[1]) return filterPeriod;

            const today = new Date();
            return [subDays(today, 7), today];
        }, [selectedPeriod]);

        useEffect(() => {
            setAnchorReady(true);
        }, []);

        const close = () => {
            if (!pending) setOpened(false);
        };

        const exportLog = async (format: RecallAuditExportFormat, period: ExportPeriod) => {
            const [from, to] = period;
            if (!from || !to) return;
            if (isAfter(from, to) || isAfter(to, addYears(from, 1))) {
                toast.error(t('pages.recallAudit.export.periodTooLong'));
                return;
            }

            try {
                setPending(true);
                const result = await onExport(format, period);
                const extension = {
                    [RecallAuditExportFormat.CSV]: 'csv',
                    [RecallAuditExportFormat.EXCEL]: 'xlsx',
                }[format];
                saveAs(result.data, `${fileName}.${extension}`);
                if (result.truncated) {
                    toast.warn(
                        t('pages.recallAudit.export.truncated', {
                            limit: result.limit ?? 10000,
                        }),
                    );
                }
                setOpened(false);
            } catch (error) {
                showRestError(error as RestError);
            } finally {
                setPending(false);
            }
        };

        return (
            <ExportPopoverAnchor ref={anchorRef}>
                <Button
                    size="sm"
                    variant={ButtonVariant.Secondary}
                    onClick={() => setOpened(true)}
                    icon={<Icon source={ExportIcon} width={16} height={16} />}
                >
                    {t('pages.recallAudit.export.action')}
                </Button>
                <Popover
                    isOpen={opened}
                    onClose={close}
                    pivotElement={anchorRef.current ?? undefined}
                    offset={{
                        x: 0,
                        y: -((anchorRef.current?.getBoundingClientRect().height ?? 0) + 12),
                    }}
                >
                    <ExportFilterContent>
                        <CloseButton
                            disabled={pending}
                            icon={
                                <Icon
                                    source={CrossIcon}
                                    fillColor="#C4C4C4"
                                    width={12}
                                    height={12}
                                />
                            }
                            onClick={close}
                        />
                        <HeaderWrapper>{t('pages.recallAudit.export.title')}</HeaderWrapper>
                        <ExportForm
                            initialPeriod={initialPeriod}
                            pending={pending}
                            onCancel={close}
                            onExport={exportLog}
                        />
                    </ExportFilterContent>
                </Popover>
            </ExportPopoverAnchor>
        );
    },
);

AuditExportControl.displayName = 'AuditExportControl';
